import React, { useEffect, useState } from 'react';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import AppsIcon from '@material-ui/icons/Apps';
import DashboardOutlinedIcon from '@material-ui/icons/DashboardOutlined';
import ChromeReaderModeOutlinedIcon from '@material-ui/icons/ChromeReaderModeOutlined';
import AssessmentOutlinedIcon from '@material-ui/icons/AssessmentOutlined';
import DesktopMacOutlinedIcon from '@material-ui/icons/DesktopMacOutlined';
import SignalCellularAltIcon from '@material-ui/icons/SignalCellularAlt';
import PeopleAltIcon from '@material-ui/icons/PeopleAlt';
import ApartmentIcon from '@material-ui/icons/Apartment';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import ExitToAppOutlinedIcon from '@material-ui/icons/ExitToAppOutlined';
import PeopleIcon from '@material-ui/icons/People';
import BubbleChartIcon from '@material-ui/icons/BubbleChart';
import AssessmentIcon from '@material-ui/icons/Assessment';

import PlaylistAddCheckIcon from '@material-ui/icons/PlaylistAddCheck';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';

import HomeIcon from '@material-ui/icons/Home';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import LiveTvIcon from '@material-ui/icons/LiveTv';
import DateRangeIcon from '@material-ui/icons/DateRange';
import ForumIcon from '@material-ui/icons/Forum';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import BarChartIcon from '@material-ui/icons/BarChart';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import SchoolIcon from '@material-ui/icons/School';
import WorkOutlineIcon from '@material-ui/icons/WorkOutline';
import AccountBoxIcon from '@material-ui/icons/AccountBox';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import OndemandVideoIcon from '@material-ui/icons/OndemandVideo';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Collapse from '@material-ui/core/Collapse';
import Divider from '@material-ui/core/Divider';
import { useHistory, useLocation } from 'react-router-dom';
import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core';
import { colors } from '../../theme/tokens';
import { useSelector, useDispatch } from 'react-redux';
import {
  hasAnyRole,
  isStudent,
  isHubManager,
  isTrainer,
} from '../../data/appRoles';
import { appPermissions, localRoutes } from '../../data/constants';
import { IState } from '../../data/types';
import { handleLogout } from '../../data/coreActions';
import { useOnboarding } from '../../modules/student/NewStudentGate';
import { get } from '../../utils/ajax';
import { remoteRoutes } from '../../data/constants';
import BrandLogo from '../BrandLogo';
import Toast from '../../utils/Toast';

// ── Shows new-message indicator — updated by CourseChat via custom event ──────
const NewPill: React.FC = () => (
  <span
    style={{
      marginLeft: 8,
      background: colors.brandTint,
      color: colors.brand,
      borderRadius: 8,
      padding: '1px 7px',
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: 0.3,
      textTransform: 'uppercase',
    }}
  >
    New
  </span>
);

const ChatUnreadBadge: React.FC = () => {
  const readStorage = () => {
    const count =
      parseInt(localStorage.getItem('elevate_chat_unread') || '0', 10) || 0;
    const hasNew = localStorage.getItem('elevate_chat_has_new') === '1';
    return { count, hasNew };
  };

  const [state, setState] = useState(readStorage);

  useEffect(() => {
    // Listen for same-tab updates from CourseChat
    const onEvent = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && typeof detail === 'object') {
        setState({ count: detail.count || 0, hasNew: !!detail.hasNew });
      } else {
        setState(readStorage());
      }
    };
    window.addEventListener('chatUnreadUpdate', onEvent);

    // Resolve last-activity timestamp across all possible backend field names
    const resolveLastAt = (r: any): string | null =>
      r.lastMessageAt ||
      r.lastActivity ||
      r.updatedAt ||
      r.lastMsg?.createdAt ||
      null;

    // Poll every 30 s to catch cross-tab activity
    const poll = () => {
      get(
        remoteRoutes.chatRooms,
        (data: any) => {
          const rooms: any[] = Array.isArray(data) ? data : [];
          const total = rooms.reduce((sum, r) => sum + (r.unreadCount || 0), 0);
          const hasNew =
            total > 0 ||
            rooms.some((r) => {
              const lastAt = resolveLastAt(r);
              const hasContent = !!(r.lastMessage || lastAt);
              if (!hasContent) return false;
              const seen = localStorage.getItem(`elevate_chat_seen_${r.id}`);
              if (!seen) return true; // never opened
              if (!lastAt) return false;
              return new Date(lastAt) > new Date(seen);
            });
          localStorage.setItem('elevate_chat_unread', String(total));
          localStorage.setItem('elevate_chat_has_new', hasNew ? '1' : '0');
          setState({ count: total, hasNew });
        },
        // Same reason as the presence heartbeat: a background poll must
        // never be able to end the session.
        () => {},
        () => {},
      );
    };
    poll();
    const t = setInterval(poll, 30_000);

    return () => {
      window.removeEventListener('chatUnreadUpdate', onEvent);
      clearInterval(t);
    };
  }, []);

  if (state.count > 0) {
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: colors.brand,
          color: colors.onBrand,
          borderRadius: 10,
          fontSize: 9,
          fontWeight: 700,
          minWidth: 16,
          height: 16,
          padding: '0 4px',
          marginLeft: 6,
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        {state.count > 99 ? '99+' : state.count}
      </span>
    );
  }
  if (state.hasNew) {
    return (
      <span
        style={{
          display: 'inline-block',
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: colors.brand,
          marginLeft: 7,
          flexShrink: 0,
        }}
      />
    );
  }
  return null;
};

interface IAppRoute {
  requiredRoles?: string[];
  name: string;
  route?: string;
  icon?: any;
  section?: string; // optional section label — renders a divider + label before this item
  children?: IAppRoute[]; // optional sub-items — parent expands/collapses instead of navigating
  locked?: boolean; // shown greyed-out with a lock icon; clicking shows a "no access" toast instead of navigating
  isNew?: boolean; // renders a small "New" pill, to point people at a recently added screen
}

// Shown to a brand-new student (trimmed signup, no enrollment yet) instead
// of the full nav below — see NewStudentGate.
const restrictedStudentRoutes: IAppRoute[] = [
  { name: 'Home', route: localRoutes.dashboard, icon: HomeIcon },
  { name: 'Browse Courses', route: localRoutes.catalog, icon: SchoolIcon },
];

const studentRoutes: IAppRoute[] = [
  { name: 'Home', route: localRoutes.dashboard, icon: HomeIcon },
  { name: 'My Courses', route: localRoutes.myCourses, icon: MenuBookIcon },
  { name: 'Live Classes', route: localRoutes.myClasses, icon: LiveTvIcon },
  { name: 'My Timetable', route: localRoutes.myTimetable, icon: DateRangeIcon },
  {
    name: 'Assessments',
    route: localRoutes.myAssignments,
    icon: FolderOpenIcon,
  },
  {
    name: 'My Blueprint',
    route: localRoutes.myBlueprint,
    icon: AccountBoxIcon,
    isNew: true,
  },
  {
    name: 'Chats / Inquiries',
    route: localRoutes.chatsInquiries,
    icon: ForumIcon,
  },
  {
    name: 'Workshops & Podcasts',
    route: localRoutes.workshops,
    icon: PlayCircleOutlineIcon,
  },
];

const staffRoutes: IAppRoute[] = [
  {
    name: 'Dashboard',
    route: localRoutes.dashboard,
    icon: DashboardOutlinedIcon,
  },
  // Course admin pages hang off this group, so it stays reachable.
  {
    name: 'Academics',
    icon: BubbleChartIcon,
    children: [
      {
        requiredRoles: [
          appPermissions.roleCourseView,
          appPermissions.roleCourseEdit,
        ],
        name: 'Courses',
        route: localRoutes.adminCourses,
        icon: BubbleChartIcon,
      },
      {
        requiredRoles: [
          appPermissions.roleClassView,
          appPermissions.roleClassEdit,
        ],
        name: 'Timetable',
        route: localRoutes.timetable,
        icon: DateRangeIcon,
      },
      {
        requiredRoles: [
          appPermissions.roleClassView,
          appPermissions.roleClassEdit,
        ],
        name: 'Assessments',
        route: localRoutes.teacherAssignments,
        icon: PlaylistAddCheckIcon,
      },
      {
        name: 'Online Course Progress',
        route: localRoutes.onlineCourseProgress,
        icon: OndemandVideoIcon,
      },
      {
        requiredRoles: [
          appPermissions.roleCourseView,
          appPermissions.roleCourseEdit,
        ],
        name: 'Announcements',
        route: localRoutes.adminAnnouncements,
        icon: NotificationsActiveIcon,
      },
      {
        name: 'Workshops & Podcasts',
        route: localRoutes.adminWorkshops,
        icon: PlayCircleOutlineIcon,
      },
    ],
  },
  {
    requiredRoles: [appPermissions.roleStudentView],
    name: 'Blueprint',
    route: localRoutes.blueprints,
    icon: ChromeReaderModeOutlinedIcon,
    isNew: true,
  },
  {
    name: 'Performance',
    route: localRoutes.performance,
    icon: AssessmentOutlinedIcon,
  },
  {
    name: 'Job Placements',
    route: localRoutes.jobPlacements,
    icon: DesktopMacOutlinedIcon,
  },
  {
    requiredRoles: [appPermissions.roleReportView],
    name: 'Reports',
    route: localRoutes.reports,
    icon: SignalCellularAltIcon,
  },
  {
    requiredRoles: [appPermissions.roleUserView, appPermissions.roleUserEdit],
    name: 'Users & Roles',
    route: localRoutes.users,
    icon: PeopleAltIcon,
  },
  {
    requiredRoles: [appPermissions.roleUserView, appPermissions.roleUserEdit],
    name: 'Learning Hubs',
    route: localRoutes.hubs,
    icon: ApartmentIcon,
  },
];

// ── Hub Manager: own-hub students, timetable, attendance, announcements ──────
const hubManagerRoutes: IAppRoute[] = [
  { name: 'Dashboard', route: localRoutes.dashboard, icon: AppsIcon },
  {
    name: 'Academics',
    icon: BubbleChartIcon,
    children: [
      { name: 'Courses', route: localRoutes.adminCourses, icon: SchoolIcon },
      {
        name: 'Timetable',
        route: localRoutes.timetable,
        icon: DateRangeIcon,
      },
      {
        name: 'Attendance',
        route: localRoutes.attendance,
        icon: AssessmentIcon,
      },
      {
        name: 'Online Course Progress',
        route: localRoutes.onlineCourseProgress,
        icon: OndemandVideoIcon,
      },
      {
        name: 'Announcements',
        route: localRoutes.adminAnnouncements,
        icon: NotificationsActiveIcon,
      },
      {
        name: 'Workshops & Podcasts',
        route: localRoutes.adminWorkshops,
        icon: PlayCircleOutlineIcon,
      },
    ],
  },
  // TEMPORARY: Job Placements is locked for Hub Managers for now — remove
  // `locked: true` (and see JobPlacements.tsx for the matching route guard)
  // to unlock.
  {
    name: 'Job Placements',
    route: localRoutes.jobPlacements,
    icon: WorkOutlineIcon,
    locked: true,
  },
  {
    name: 'Performance',
    route: localRoutes.performance,
    icon: TrendingUpIcon,
  },
  { name: 'Students', route: localRoutes.students, icon: PeopleIcon },
  {
    name: 'Blueprints',
    route: localRoutes.blueprints,
    icon: AccountBoxIcon,
    isNew: true,
  },
  {
    name: 'Chats / Inquiries',
    route: localRoutes.chatsInquiries,
    icon: ForumIcon,
  },
  { name: 'Reports', route: localRoutes.hubReports, icon: BarChartIcon },
];

// ── Trainer: instructor portal nav ───────────────────────────────────────────
const trainerRoutes: IAppRoute[] = [
  { name: 'Dashboard', route: localRoutes.dashboard, icon: AppsIcon },
  {
    name: 'Academics',
    icon: BubbleChartIcon,
    children: [
      { name: 'Courses', route: localRoutes.trainerCourses, icon: SchoolIcon },
      {
        name: 'Timetable',
        route: localRoutes.myTimetable,
        icon: DateRangeIcon,
      },
      {
        name: 'Resources',
        route: localRoutes.trainerResources,
        icon: FolderOpenIcon,
      },
      {
        name: 'Online Course Progress',
        route: localRoutes.onlineCourseProgress,
        icon: OndemandVideoIcon,
      },
      {
        name: 'Announcements',
        route: localRoutes.trainerAnnouncements,
        icon: NotificationsActiveIcon,
      },
      {
        name: 'Workshops & Podcasts',
        route: localRoutes.workshops,
        icon: PlayCircleOutlineIcon,
      },
    ],
  },
  { name: 'Students', route: localRoutes.trainerStudents, icon: PeopleIcon },
  {
    name: 'Performance',
    route: localRoutes.performance,
    icon: TrendingUpIcon,
  },
  {
    name: 'Chats / Inquiries',
    route: localRoutes.chatsInquiries,
    icon: ForumIcon,
  },
  {
    name: 'Reports',
    route: localRoutes.trainerAnalytics,
    icon: BarChartIcon,
  },
];

const selectedBg = colors.navItemActiveBg;

const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: colors.bgSurface,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderRight: `1px solid ${colors.border}`,
    },
    logoHolder: {
      height: 88,
      padding: '0 24px',
      display: 'flex',
      alignItems: 'center',
      borderBottom: `1px solid ${colors.border}`,
    },
    logoImg: {
      height: 40,
      objectFit: 'contain' as const,
    },
    list: {
      flex: 1,
      paddingTop: 12,
      overflowY: 'auto' as const,
      // A slim bar in the sidebar's own colours instead of the system's
      // thick grey one, which only shows when the window is short.
      scrollbarWidth: 'thin' as const,
      scrollbarColor: `${colors.border} transparent`,
      '&::-webkit-scrollbar': { width: 6 },
      '&::-webkit-scrollbar-track': { background: 'transparent' },
      '&::-webkit-scrollbar-thumb': {
        background: colors.border,
        borderRadius: 3,
      },
    },
    bottomList: { paddingBottom: 12 },
    footerDivider: { margin: '0 18px', backgroundColor: colors.border },
    item: {
      borderRadius: 10,
      margin: '3px 18px',
      width: 'calc(100% - 36px)',
      padding: '10px 14px',
      '&:hover': { backgroundColor: colors.hover },
    },
    selectedItem: {
      backgroundColor: `${selectedBg} !important`,
    },
    icon: { minWidth: 36, color: colors.navItemText },
    iconSelected: { minWidth: 36, color: colors.navItemActive },
    // Settings and Logout read brighter than the page links above them.
    footerIcon: { minWidth: 36, color: colors.textSecondary },
    footerText: {
      color: colors.textSecondary,
      '& span': {
        fontFamily: '"Inter Tight", sans-serif',
        fontWeight: 500,
        fontSize: '14px',
      },
    },
    sectionLabel: {
      padding: '14px 20px 4px',
      fontSize: 10,
      fontWeight: 700,
      color: colors.textFaint,
      letterSpacing: '0.09em',
      textTransform: 'uppercase' as const,
      lineHeight: 1,
    },
    text: {
      color: colors.navItemText,
      '& span': {
        fontFamily: '"Inter Tight", sans-serif',
        fontWeight: 500,
        fontSize: '14px',
      },
    },
    textSelected: {
      color: colors.navItemActive,
      '& span': { fontWeight: 600 },
    },
  }),
);

const StyledListItem = withStyles({
  root: {
    '&$selected': { backgroundColor: selectedBg },
    '&$selected:hover': { backgroundColor: selectedBg },
  },
  selected: {},
})(ListItem);

const NavMenu = (props: any) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state: IState) => state.core.user);
  const student = isStudent(user);
  const hubManager = isHubManager(user);
  const trainer = isTrainer(user);
  const { hasEnrollments } = useOnboarding();

  const onClick = (path: string) => () => {
    history.push(path);
    if (props.onClose) props.onClose();
  };

  const doLogout = () => dispatch(handleLogout());

  const isSelected = (pathStr: string) =>
    location.pathname.indexOf(pathStr) > -1;

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const allowedByRole = (it: IAppRoute) =>
    it.requiredRoles ? hasAnyRole(user, it.requiredRoles) : true;

  const cleanRoutes = (r: IAppRoute[]): IAppRoute[] =>
    r
      .filter(allowedByRole)
      .map((it) =>
        it.children
          ? { ...it, children: it.children.filter(allowedByRole) }
          : it,
      )
      .filter((it) => !it.children || it.children.length > 0);

  const Logo = (
    <div className={classes.logoHolder}>
      <BrandLogo className={classes.logoImg} />
    </div>
  );

  // ── Student sidebar ──────────────────────────────────────────────────────
  if (student) {
    // A brand-new student with no enrollment yet only sees Home + Browse
    // Courses — see NewStudentGate.
    const items = hasEnrollments ? studentRoutes : restrictedStudentRoutes;
    return (
      <div className={classes.root}>
        {Logo}
        <List className={classes.list}>
          {items.map((it) => {
            const Icon = it.icon;
            const selected = isSelected(it.route!);
            const isChat = it.route === localRoutes.chatsInquiries;
            return (
              <StyledListItem
                button
                key={it.name}
                onClick={onClick(it.route!)}
                selected={selected}
                className={classes.item}
                classes={{ selected: classes.selectedItem }}
              >
                <ListItemIcon
                  className={selected ? classes.iconSelected : classes.icon}
                >
                  <Icon style={{ fontSize: 19 }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      {it.name}
                      {isChat && <ChatUnreadBadge />}
                      {it.isNew && <NewPill />}
                    </span>
                  }
                  className={selected ? classes.textSelected : classes.text}
                />
              </StyledListItem>
            );
          })}
        </List>

        <Divider className={classes.footerDivider} />

        <List className={classes.bottomList}>
          {/* A brand-new student with nothing enrolled yet has no account
              settings worth visiting; Logout stays available regardless. */}
          {(hasEnrollments
            ? [
                {
                  name: 'Settings',
                  route: localRoutes.settings,
                  Icon: SettingsOutlinedIcon,
                },
              ]
            : []
          ).map(({ name, route, Icon }) => {
            const selected = isSelected(route);
            return (
              <StyledListItem
                button
                key={name}
                onClick={onClick(route)}
                selected={selected}
                className={classes.item}
                classes={{ selected: classes.selectedItem }}
              >
                <ListItemIcon
                  className={
                    selected ? classes.iconSelected : classes.footerIcon
                  }
                >
                  <Icon style={{ fontSize: 19 }} />
                </ListItemIcon>
                <ListItemText
                  primary={name}
                  className={
                    selected ? classes.textSelected : classes.footerText
                  }
                />
              </StyledListItem>
            );
          })}
          <StyledListItem button onClick={doLogout} className={classes.item}>
            <ListItemIcon className={classes.footerIcon}>
              <ExitToAppOutlinedIcon style={{ fontSize: 19 }} />
            </ListItemIcon>
            <ListItemText primary="Logout" className={classes.footerText} />
          </StyledListItem>
        </List>
      </div>
    );
  }

  // Helper: render a route list, inserting section labels where defined and
  // expanding items with `children` into a collapsible group on click.
  const renderRouteList = (routes: IAppRoute[]) =>
    routes.map((it) => {
      const Icon = it.icon;

      if (it.children) {
        const hasSelectedChild = it.children.some(
          (c) => c.route && isSelected(c.route),
        );
        const open = expanded[it.name] ?? hasSelectedChild;
        return (
          <React.Fragment key={it.name}>
            {it.section && (
              <div className={classes.sectionLabel}>{it.section}</div>
            )}
            <StyledListItem
              button
              onClick={() =>
                setExpanded((prev) => ({ ...prev, [it.name]: !open }))
              }
              className={classes.item}
            >
              <ListItemIcon className={classes.icon}>
                <Icon style={{ fontSize: 19 }} />
              </ListItemIcon>
              <ListItemText primary={it.name} className={classes.text} />
              {open ? (
                <ExpandLessIcon
                  style={{ color: colors.iconMuted, fontSize: 20 }}
                />
              ) : (
                <ExpandMoreIcon
                  style={{ color: colors.iconMuted, fontSize: 20 }}
                />
              )}
            </StyledListItem>
            <Collapse in={open} timeout="auto" unmountOnExit>
              {it.children.map((child) => {
                const ChildIcon = child.icon;
                const childSelected = isSelected(child.route!);
                return (
                  <StyledListItem
                    button
                    key={child.name}
                    onClick={onClick(child.route!)}
                    selected={childSelected}
                    className={classes.item}
                    classes={{ selected: classes.selectedItem }}
                    style={{ paddingLeft: 28 }}
                  >
                    <ListItemIcon
                      className={
                        childSelected ? classes.iconSelected : classes.icon
                      }
                    >
                      <ChildIcon style={{ fontSize: 17 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                          {child.name}
                          {child.isNew && <NewPill />}
                        </span>
                      }
                      className={
                        childSelected ? classes.textSelected : classes.text
                      }
                    />
                  </StyledListItem>
                );
              })}
            </Collapse>
          </React.Fragment>
        );
      }

      const selected = isSelected(it.route!);
      const isChat = it.route === localRoutes.chatsInquiries;
      if (it.locked) {
        return (
          <React.Fragment key={it.name}>
            {it.section && (
              <div className={classes.sectionLabel}>{it.section}</div>
            )}
            <StyledListItem
              button
              onClick={() => Toast.error("You don't have access to this yet")}
              className={classes.item}
              style={{ opacity: 0.45, cursor: 'not-allowed' }}
            >
              <ListItemIcon className={classes.icon}>
                <Icon style={{ fontSize: 19 }} />
              </ListItemIcon>
              <ListItemText primary={it.name} className={classes.text} />
              <LockOutlinedIcon
                style={{
                  fontSize: 15,
                  color: colors.textFaint,
                  marginRight: 4,
                }}
              />
            </StyledListItem>
          </React.Fragment>
        );
      }
      return (
        <React.Fragment key={it.name}>
          {it.section && (
            <div className={classes.sectionLabel}>{it.section}</div>
          )}
          <StyledListItem
            button
            onClick={onClick(it.route!)}
            selected={selected}
            className={classes.item}
            classes={{ selected: classes.selectedItem }}
          >
            <ListItemIcon
              className={selected ? classes.iconSelected : classes.icon}
            >
              <Icon style={{ fontSize: 19 }} />
            </ListItemIcon>
            <ListItemText
              primary={
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  {it.name}
                  {isChat && <ChatUnreadBadge />}
                  {it.isNew && <NewPill />}
                </span>
              }
              className={selected ? classes.textSelected : classes.text}
            />
          </StyledListItem>
        </React.Fragment>
      );
    });

  const BottomActions = (
    <>
      {(() => {
        const selected = isSelected(localRoutes.settings);
        return (
          <StyledListItem
            button
            onClick={onClick(localRoutes.settings)}
            selected={selected}
            className={classes.item}
            classes={{ selected: classes.selectedItem }}
          >
            <ListItemIcon
              className={selected ? classes.iconSelected : classes.footerIcon}
            >
              <SettingsOutlinedIcon style={{ fontSize: 19 }} />
            </ListItemIcon>
            <ListItemText
              primary="Settings"
              className={selected ? classes.textSelected : classes.footerText}
            />
          </StyledListItem>
        );
      })()}
      <StyledListItem button onClick={doLogout} className={classes.item}>
        <ListItemIcon className={classes.footerIcon}>
          <ExitToAppOutlinedIcon style={{ fontSize: 19 }} />
        </ListItemIcon>
        <ListItemText primary="Logout" className={classes.footerText} />
      </StyledListItem>
    </>
  );

  // ── Hub Manager sidebar ──────────────────────────────────────────────────
  if (hubManager) {
    return (
      <div className={classes.root}>
        {Logo}
        <List className={classes.list}>
          {renderRouteList(hubManagerRoutes)}
        </List>
        <Divider className={classes.footerDivider} />
        <List className={classes.bottomList}>{BottomActions}</List>
      </div>
    );
  }

  // ── Trainer sidebar ──────────────────────────────────────────────────────
  if (trainer) {
    return (
      <div className={classes.root}>
        {Logo}
        <div
          style={{
            padding: '8px 20px 10px',
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: colors.textFaint,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            Instructor Portal
          </span>
        </div>
        <List className={classes.list}>{renderRouteList(trainerRoutes)}</List>
        <Divider className={classes.footerDivider} />
        <List className={classes.bottomList}>{BottomActions}</List>
      </div>
    );
  }

  // ── Super Admin / Admin sidebar ──────────────────────────────────────────
  const finalRoutes = cleanRoutes(staffRoutes);

  return (
    <div className={classes.root}>
      {Logo}
      <List className={classes.list}>{renderRouteList(finalRoutes)}</List>
      <Divider className={classes.footerDivider} />
      <List className={classes.bottomList}>{BottomActions}</List>
    </div>
  );
};

export default NavMenu;
